import { Alert as MuiAlert, AlertTitle, styled, Typography, useTheme } from '@mui/material'
import { CheckCircleIcon, InfoIcon, WarningDiamondIcon, WarningIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { ComponentProps } from 'react'

type AlertProps = {
    title?: string
    children: React.ReactNode
    size?: 'small' | 'medium'
} & ComponentProps<typeof MuiAlert>

export function Alert({ title, children, size = 'medium', icon, ...props }: AlertProps) {
    const theme = useTheme()

    const colorMapping = {
        error: theme.palette.error.main,
        warning: theme.palette.warning.main,
        info: theme.palette.info.main,
        success: theme.palette.success.main,
    }

    return (
        <StyledAlert
            iconMapping={{
                error: <WarningDiamondIcon />,
                info: <InfoIcon />,
                success: <CheckCircleIcon />,
                warning: <WarningIcon />,
            }}
            icon={icon}
            $color={colorMapping[props.severity || 'success']}
            $size={size}
            {...props}
        >
            {title && (
                <AlertTitle>
                    <Typography variant={'headline3'} color={theme.palette.customtext.black100}>
                        {title}
                    </Typography>
                </AlertTitle>
            )}
            {children}
        </StyledAlert>
    )
}

const StyledAlert = styled(MuiAlert, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: string; $size: AlertProps['size'] }>(({ theme, $color, $size }) => ({
    position: 'relative',
    marginBottom: theme.spacing(4),
    padding: $size === 'medium' ? '24px' : tokens.SizingSpacing100,
    border: `1px solid ${$color}`,
    '& .MuiAlert-icon': {
        width: tokens.SizingIconM,
        height: tokens.SizingIconM,
    },
    '& .MuiAlert-action': {
        width: '40px',
        height: '40px',
        position: 'absolute',
        right: '7px',
        top: '0px',
        '& svg': {
            color: theme.palette.customtext.black60,
        },
        padding: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '&:hover': {
            '& svg': {
                color: theme.palette.customtext.black100,
            },
        },
    },
}))
